import * as Sentry from '@sentry/react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

// Import layouts
import MainLayout from './layouts/MainLayout';

// Import pages
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import VerifyEmail from './pages/Auth/VerifyEmail';
import ForgotPassword from './pages/Auth/ForgotPassword';
import ResetPassword from './pages/Auth/ResetPassword';
import SsoCallback from './pages/Auth/SsoCallback';
import Dashboard from './pages/Dashboard';
import DocumentEditor from './pages/DocumentEditor';
import Documents from './pages/Documents';
import SigningRequests from './pages/SigningRequests';
import DocumentViewer from './pages/DocumentViewer';
import Profile from './pages/Profile';
import AdminUsers from './pages/Admin/Users';
import AdminCategories from './pages/Admin/Categories';
import SignatureEditor from './pages/SignatureEditor';
import Templates from './pages/Templates';
import TemplateEditor from './pages/TemplateEditor';
import Recipients from './pages/Recipients';
import Settings from './pages/Settings';
import QuickSend from './pages/QuickSend';
import TrackDocument from './pages/TrackDocument';
import SigningGate from './pages/SigningGate';

// Import auth context
import { AuthProvider, useAuthContext } from './contexts/AuthContext';
import { PlanProvider } from './contexts/PlanContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Import Toaster component
import { Toaster } from './components/ui/toaster';

// Auth guard component to protect routes
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuthContext();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary rounded-full border-t-transparent"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <>{children}</>;
};

// Redirect authenticated users away from auth pages
const RedirectIfAuthenticated = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, loading } = useAuthContext();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary rounded-full border-t-transparent"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

function AppRoutes() {
  return (
    <Routes>
      <Route 
        path="/login" 
        element={
          <RedirectIfAuthenticated>
            <Login />
          </RedirectIfAuthenticated>
        } 
      />
      <Route
        path="/join"
        element={
          <RedirectIfAuthenticated>
            <Register />
          </RedirectIfAuthenticated>
        }
      />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/auth/callback" element={<SsoCallback />} />

      {/* Public quick-send routes */}
      <Route path="/hi" element={<QuickSend lang="en" />} />
      <Route path="/bonjour" element={<QuickSend lang="fr" />} />
      <Route path="/track/:code" element={<TrackDocument />} />
      <Route path="/sign/:token" element={<SigningGate />} />
      
      {/* Protected routes inside MainLayout */}
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="document-editor" element={<DocumentEditor />} />
        <Route path="document-editor/:id" element={<DocumentEditor />} />
        <Route path="documents" element={<Documents />} />
        <Route path="templates" element={<Templates />} />
        <Route path="template-editor" element={<TemplateEditor />} />
        <Route path="template-editor/:id" element={<TemplateEditor />} />
        <Route path="signing-requests" element={<SigningRequests />} />
        <Route path="document-viewer/:documentId" element={<DocumentViewer />} />
        <Route path="profile" element={<Profile />} />
        <Route path="signature-editor" element={<SignatureEditor />} />
        <Route path="recipients" element={<Recipients />} />
        <Route path="settings" element={<Settings />} />
        <Route path="admin/users" element={<AdminUsers />} />
        <Route path="admin/categories" element={<AdminCategories />} />
      </Route>
      
      {/* Fallback route */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<SentryFallback />}>
      <ThemeProvider>
        <AuthProvider>
          <PlanProvider>
            <DndProvider backend={HTML5Backend}>
              <Router>
                <AppRoutes />
                <Toaster />
              </Router>
            </DndProvider>
          </PlanProvider>
        </AuthProvider>
      </ThemeProvider>
    </Sentry.ErrorBoundary>
  );
}

function SentryFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center max-w-md px-6">
        <h1 className="text-2xl font-bold text-foreground mb-2">Something went wrong</h1>
        <p className="text-muted-foreground mb-4">
          An unexpected error occurred. Our team has been notified.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

export default App;
